import { SizableText, styled } from "tamagui";

/**
 * Centralized typographic scale — the single source of truth for text roles.
 *
 * Every role sets only `size` (a font token). Tamagui pairs each size token
 * with its matching `lineHeight` token from `tamagui.config.ts`, so leading
 * stays consistent everywhere and follows the same responsive path on mobile
 * and web. Breakpoints (`$gtSm`, `$gtMd`) scale the step up on larger screens
 * the way Apple's type ramps: bigger size + proportionally tighter leading.
 *
 * Colors are intentionally left to the call site (defaults inherit), so the
 * same role can render dark-on-light or themed text without new variants.
 */

/** Hero / page-level title (course + dashboard headers). 22 → 28 → 32. */
export const PageTitle = styled(SizableText, {
  name: "PageTitle",
  ff: "$body",
  fontWeight: "600",
  letterSpacing: 0,
  size: "$22",
  $gtSm: { size: "$8" },
  $gtMd: { size: "$9" },
});

/** In-content main title (lesson page, collection body). 20 → 24. */
export const ContentTitle = styled(SizableText, {
  name: "ContentTitle",
  ff: "$body",
  fontWeight: "600",
  letterSpacing: 0,
  size: "$6",
  $gtMd: { size: "$7" },
});

/** Prominent subtitle under a hero title (e.g. instructor names). 14 → 18. */
export const SubtitleText = styled(SizableText, {
  name: "SubtitleText",
  ff: "$body",
  fontWeight: "600",
  size: "$3",
  $gtSm: { size: "$5" },
});

/** Section heading inside a page ("All courses", "Search results"). 18 → 20. */
export const SectionTitle = styled(SizableText, {
  name: "SectionTitle",
  ff: "$body",
  fontWeight: "700",
  size: "$5",
  $gtMd: { size: "$6" },
});

/** Card / list-row title. 14 → 16. */
export const CardTitle = styled(SizableText, {
  name: "CardTitle",
  ff: "$body",
  fontWeight: "700",
  size: "$3",
  $gtMd: { size: "$4" },
});

/** Eyebrow / meta line (instructor names, "Collection" label). 12 → 14. */
export const EyebrowText = styled(SizableText, {
  name: "EyebrowText",
  ff: "$body",
  fontWeight: "600",
  size: "$2",
  $gtMd: { size: "$3" },
});

/** Body copy / summaries. 14 → 16. */
export const BodyText = styled(SizableText, {
  name: "BodyText",
  ff: "$body",
  fontWeight: "400",
  size: "$3",
  $gtMd: { size: "$4" },
});

/** Small caption / counts. 12 → 14. */
export const CaptionText = styled(SizableText, {
  name: "CaptionText",
  ff: "$body",
  fontWeight: "400",
  size: "$2",
  $gtMd: { size: "$3" },
});
